<template>
	<div class="app-input-wrapper">
		<label @click="focusOnInputEl">
			<div v-if="label" class="app-input-label">
				{{ label }}
				<span v-if="requiredField" class="app-input-required">*</span>
			</div>
			<input
				ref="inputEl"
				v-model.trim="model"
				class="app-input-element"
				:class="{
					'input-valid': !error?.length,
					'input-invalid': error?.length,
					'input-disabled': disabled,
					'input-with-icon': iconName,
				}"
				:type="type"
				:name="name"
				:disabled="disabled"
				:placeholder="placeholder"
				:maxlength="maxlength"
				:max
				:min
				@blur="handleBlur"
				@change="emits('change', $event)"
				@input="emits('input', $event)"
				@focus="handleFocus($event)"
			/>

			<Icon v-if="iconName" :name="'local:' + iconName" class="app-input-icon" />
		</label>

		<div class="app-input-error">
			<span v-if="error">{{ error }}</span>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { ref, useTemplateRef, onMounted } from 'vue';

	interface IProps {
		label?: string;
		placeholder?: string;
		name?: string;
		error?: string;
		disabled?: boolean;
		type?: string;
		maxlength?: number;
		focusOnMount?: boolean;
		requiredField?: boolean;
		iconName?: string;
		min?: number;
		max?: number;
	}

	const props = withDefaults(defineProps<IProps>(), {
		label: '',
		placeholder: '',
		name: '',
		error: undefined,
		disabled: false,
		type: 'text',
		maxlength: Number.MAX_SAFE_INTEGER,
		requiredField: false,
		iconName: '',
	});

	const model = defineModel<string | number>({ required: true, default: '' });
	const emits = defineEmits(['blur', 'change', 'input', 'focus']);

	const isFocused = ref(false);
	const inputEl = useTemplateRef('inputEl');

	onMounted(() => {
		if (props.focusOnMount) {
			focusOnInputEl();
		}
	});

	function handleFocus($event: FocusEvent) {
		emits('focus', $event);
		isFocused.value = true;
	}

	function handleBlur($event: FocusEvent) {
		emits('blur', $event);
		isFocused.value = false;
	}

	function focusOnInputEl() {
		inputEl.value?.focus();
	}
</script>

<style scoped lang="scss"></style>
